import { useEffect, useRef, useState, RefObject } from 'react'

interface ScrollRevealOptions {
  /** IntersectionObserver threshold (0–1). Default: 0.15 */
  threshold?: number
  /** Root margin. Default: '0px 0px -60px 0px' (triggers slightly before full view) */
  rootMargin?: string
  /** Once revealed, stay revealed? Default: true */
  once?: boolean
}

interface ScrollRevealResult {
  /** Whether the element is currently revealed */
  isRevealed: boolean
  /** How far into view (0–1) — useful for parallax */
  ratio: number
  /** Ref to attach to the element */
  ref: RefObject<HTMLElement>
}

/**
 * Hook for scroll-driven reveal animations.
 * Returns `isRevealed` for CSS class toggling and `ratio` for continuous scroll effects.
 */
export function useScrollReveal(options: ScrollRevealOptions = {}): ScrollRevealResult {
  const { threshold = 0.15, rootMargin = '0px 0px -60px 0px', once = true } = options
  const ref = useRef<HTMLElement>(null)
  const [isRevealed, setIsRevealed] = useState(false)
  const [ratio, setRatio] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const r = entry.intersectionRatio
        setRatio(r)

        if (entry.isIntersecting && r >= threshold) {
          setIsRevealed(true)
          if (once) observer.unobserve(el)
        } else if (!once) {
          setIsRevealed(false)
        }
      },
      {
        threshold: [0, 0.1, 0.15, 0.25, 0.5, 0.75, 1],
        rootMargin,
      }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold, rootMargin, once])

  return { isRevealed, ratio, ref }
}

/**
 * CSS classes for scroll-reveal animations.
 * Apply these to elements alongside useScrollReveal.
 *
 * Usage:
 *   const { isRevealed, ref } = useScrollReveal()
 *   <div ref={ref} className={isRevealed ? REVEAL.visible : REVEAL.hidden}>
 */
export const REVEAL = {
  /** Hidden state — element is below viewport, waiting */
  hidden: 'opacity-0 translate-y-8 scale-[0.98]',
  /** Visible state — element has entered viewport */
  visible: 'opacity-100 translate-y-0 scale-100',
  /** Transition classes — apply always */
  transition: 'transition-all duration-700 ease-out',
  /** Stagger delays for children */
  delay100: 'delay-100',
  delay200: 'delay-200',
  delay300: 'delay-300',
  delay400: 'delay-400',
  delay500: 'delay-500',
} as const
